import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Download } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";

export default function PanelFunding() {
    const [ledger, setLedger] = useState([]);
    const [err, setErr] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [search, setSearch] = useState("");

    const refresh = async () => {
        setErr("");
        try {
            const r = await api.get("/partner/payouts/ledger");
            setLedger(r.data.items || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { refresh(); }, []);

    const credits = useMemo(() => {
        const rows = ledger.filter((x) => x.direction === "credit");
        return rows.filter((r) => {
            if (from && new Date(r.created_at) < new Date(from)) return false;
            if (to && new Date(r.created_at) > new Date(`${to}T23:59:59`)) return false;
            if (search) {
                const s = search.toLowerCase();
                return (
                    (r.ref_id || "").toLowerCase().includes(s) ||
                    (r.note || "").toLowerCase().includes(s) ||
                    (r.reason || "").toLowerCase().includes(s)
                );
            }
            return true;
        });
    }, [ledger, from, to, search]);

    const totalReceived = credits.reduce((s, r) => s + r.amount, 0);
    const totalFees = 0;
    const totalCredited = totalReceived - totalFees;

    const exportCsv = () => {
        const rows = [
            ["created_at", "ref_id", "note", "reason", "actor", "amount"],
            ...credits.map((r) => [r.created_at, r.ref_id || "", r.note || "", r.reason, r.actor, r.amount]),
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `funding-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="panel-funding" className="p-6 space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Funding History</h1>
                    <p className="text-xs text-slate-500 mt-1">All payments received on your account, with fees</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={refresh} data-testid="funding-refresh"
                            className="btn-secondary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </button>
                    <button onClick={exportCsv} data-testid="funding-download"
                            className="btn-primary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                        <Download className="w-3.5 h-3.5" /> Download Excel
                    </button>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">From</div>
                    <input data-testid="funding-from" type="date" value={from}
                            onChange={(e) => setFrom(e.target.value)}
                            className="input text-sm mt-1 w-full" />
                </div>
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">To</div>
                    <input data-testid="funding-to" type="date" value={to}
                            onChange={(e) => setTo(e.target.value)}
                            className="input text-sm mt-1 w-full" />
                </div>
                <div className="flex-[2]">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Search</div>
                    <input data-testid="funding-search" value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="UTR / payer name / payer account"
                            className="input text-sm mt-1 w-full" />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <Stat label="Total Received" value={fmtINR(totalReceived)} tone="blue" testid="funding-total-received" />
                <Stat label="Total Fees Deducted" value={fmtINR(totalFees)} tone="amber" testid="funding-total-fees" />
                <Stat label="Total Credited to Wallet" value={fmtINR(totalCredited)} tone="emerald" testid="funding-total-credited" />
            </div>

            {err && <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
                {credits.length === 0 ? (
                    <p data-testid="funding-empty" className="text-sm text-slate-400 text-center py-10">
                        No funding yet. Share your account details with payers to start receiving payments.
                    </p>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                            <tr>
                                <th className="px-3 py-2 text-left">When</th>
                                <th className="px-3 py-2 text-left">Ref</th>
                                <th className="px-3 py-2 text-left">Note</th>
                                <th className="px-3 py-2 text-left">Actor</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {credits.map((r) => (
                                <tr key={r.entry_id} data-testid={`funding-row-${r.entry_id}`} className="border-t border-slate-100">
                                    <td className="px-3 py-2 text-slate-500 text-[11px]">{new Date(r.created_at).toLocaleString()}</td>
                                    <td className="px-3 py-2 font-mono">{r.ref_id || "—"}</td>
                                    <td className="px-3 py-2">{r.note || r.reason}</td>
                                    <td className="px-3 py-2 text-[11px] text-slate-500">{r.actor}</td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">+{fmtINR(r.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, tone, testid }) {
    const tones = {
        blue: "bg-white border-slate-200 text-blue-700",
        amber: "bg-white border-slate-200 text-amber-700",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    };
    return (
        <div data-testid={testid} className={`${tones[tone]} border rounded-xl p-4`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="text-2xl font-bold mt-1 font-mono">{value}</div>
        </div>
    );
}
